import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { AlchamisMark } from "@/components/AlchamisMark";
import GlobalContactBlock from "@/components/GlobalContactBlock";
import { ALCHAMIS_PRIMARY_EMAIL } from "@/components/FlagIcon";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-24">
      <Seo title="About AlchamisGroup" description="A consulting house with an engineering soul — founded by Latifa Richards." path="/about" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-7">
            <span className="overline">The Firm</span>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">
              A consulting house
              <span className="block italic gold-text">with an engineering soul.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-5 text-[#3F4A6B] leading-relaxed">
            <p>
              Founded by operators who built and scaled regulated technology businesses, AlchamisGroup
              exists for leaders who refuse the false choice between elegant strategy and rigorous
              execution.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="relative">
            <img
              src="/founder.jpg"
              alt="Latifa Richards"
              className="w-full aspect-[4/5] object-cover border border-[#001B54]/10"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#000B24] to-transparent">
              <div className="font-display text-2xl text-[#001B54]">Latifa Richards</div>
              <div className="text-gold/80 text-xs tracking-[0.22em] uppercase mt-1">Founder &amp; Chief Executive</div>
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7 space-y-6 text-[#3F4A6B] text-base sm:text-lg leading-relaxed">
          <p>
            Latifa Richards founded AlchamisGroup with a single ambition — to assemble the consulting,
            technology, and AI capabilities that operating executives actually need, under one
            partnership-led house.
          </p>
          <p>
            From our New Jersey headquarters, she leads a firm that blends top-tier advisory with an
            in-house portfolio of enterprise platforms — building the rare consulting house that ships
            both strategy and software with the same hand.
          </p>
          <blockquote className="border-l-2 border-gold pl-6 italic font-display text-2xl text-[#001B54]">
            “Our clients hire us when they cannot afford to be wrong. That is the standard we hold ourselves to.”
          </blockquote>
        </div>
      </section>

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <span className="overline">Principles</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-tight">What we hold sacred.</h2>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Partner-led from day one", text: "Every engagement is led personally by a senior partner. No bench staffing surprises." },
              { title: "We ship what we recommend", text: "Strategy without execution is theatre. Our engineers stand behind every blueprint." },
              { title: "Adoption is the outcome", text: "Value is measured at the human layer — by how decisively your operators adopt the new model." },
              { title: "Discretion as a discipline", text: "We treat client information the way private banks treat their books — by default." },
            ].map((p) => (
              <div key={p.title} className="border border-[#001B54]/10 p-6">
                <AlchamisMark size={20} />
                <div className="font-display text-2xl text-[#001B54] mt-4">{p.title}</div>
                <div className="text-[#3F4A6B] text-sm mt-3 leading-relaxed">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">Contact</span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] mt-6 leading-tight">Headquartered in New Jersey.</h2>
          <p className="text-[#3F4A6B] mt-6 leading-relaxed">Engagements delivered globally.</p>
        </div>
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <div className="flex gap-4 items-start p-6 border border-[#001B54]/10">
            <MapPin className="text-gold mt-1" size={18} />
            <div>
              <div className="text-[#001B54]">90 E Halsey Rd, Parsippany, Suite 332</div>
              <div className="text-[#3F4A6B] text-sm">New Jersey, USA</div>
            </div>
          </div>
          <div className="flex gap-4 items-center p-6 border border-[#001B54]/10">
            <Mail className="text-gold" size={18} />
            <a className="text-[#001B54] hover:text-gold" href={`mailto:${ALCHAMIS_PRIMARY_EMAIL}`} data-testid="about-email">{ALCHAMIS_PRIMARY_EMAIL}</a>
          </div>
          <GlobalContactBlock variant="stack" testIdPrefix="about-phone" />
          <Link to="/contact" className="luxury-button mt-2">Request a Private Briefing</Link>
        </div>
      </section>
    </div>
  );
}
